import React, { useEffect } from "react";
import AlertsCenter from "@/components/alerts/AlertsCenter";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import { useAlerts } from "@/hooks/useAlerts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import {
  Bell,
  Settings,
  Shield,
  Zap,
  Activity,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

const AlertsPage: React.FC = () => {
  const { stats, requestNotificationPermission } = useAlerts();
  const { filter: filterState } = useGlobalFilter();

  useEffect(() => {
    const checkPermission = async () => {
      if ("Notification" in window && Notification.permission === "default") {
        setTimeout(async () => {
          await requestNotificationPermission();
        }, 2000); // Aguardar 2 segundos para não ser intrusivo
      }
    };

    checkPermission();
  }, [requestNotificationPermission]);

  return (
    <IntegrationErrorBoundary
      integrationType="general"
      integrationId="alerts-page"
    >
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Bell className="h-8 w-8" />
              Sistema de Alertas Inteligentes
            </h1>
            <p className="text-muted-foreground">
              Monitoramento proativo e resolução automatizada de problemas nas
              integrações
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </Button>
          </div>
        </div>

        <GlobalFilter />

        {/* Cards de Status Geral */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card
            className={stats.critical > 0 ? "border-red-200 bg-red-50" : ""}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Alertas Críticos
                  </p>
                  <p
                    className={`text-3xl font-bold ${stats.critical > 0 ? "text-red-600" : "text-green-600"}`}
                  >
                    {stats.critical}
                  </p>
                </div>
                <AlertTriangle
                  className={`h-8 w-8 ${stats.critical > 0 ? "text-red-600" : "text-gray-400"}`}
                />
              </div>
              {stats.critical > 0 && (
                <div className="mt-2">
                  <Badge variant="destructive" className="animate-pulse">
                    Requer atenção imediata
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Total de Alertas
                  </p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <Bell className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="mt-2">
                <Badge variant="outline">{stats.unread} não lidos</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Status do Sistema
                  </p>
                  <p className="text-xl font-bold">
                    {stats.critical === 0 ? "Estável" : "Instável"}
                  </p>
                </div>
                {stats.critical === 0 ? (
                  <CheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <Activity className="h-8 w-8 text-red-600" />
                )}
              </div>
              <div className="mt-2">
                <Badge
                  variant={stats.critical === 0 ? "default" : "destructive"}
                  className={
                    stats.critical === 0 ? "bg-green-100 text-green-800" : ""
                  }
                >
                  {stats.critical === 0
                    ? "Todas integrações OK"
                    : "Problemas detectados"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Auto-resolução
                  </p>
                  <p className="text-xl font-bold">Ativa</p>
                </div>
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-800">
                  Sistema inteligente
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Informações sobre o Sistema */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Como Funciona
              </CardTitle>
              <CardDescription>
                Sistema de monitoramento proativo das suas integrações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <Activity className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">Monitoramento 24/7</h4>
                  <p className="text-sm text-muted-foreground">
                    Verificação automática da saúde das integrações a cada 30
                    segundos
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-yellow-100 p-2 rounded-full">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-medium">Detecção Inteligente</h4>
                  <p className="text-sm text-muted-foreground">
                    Identifica problemas baseado em padrões e métricas
                    específicas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <Zap className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Auto-resolução</h4>
                  <p className="text-sm text-muted-foreground">
                    Tentativas automáticas de correção para problemas conhecidos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Categorias de Alertas</CardTitle>
              <CardDescription>
                Tipos de problemas monitorados pelo sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3">
                <div className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <span>🔌</span>
                    <span className="text-sm font-medium">Conexão</span>
                  </div>
                  <Badge variant="outline">
                    {stats.byCategory.connection || 0}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <span>📊</span>
                    <span className="text-sm font-medium">Dados</span>
                  </div>
                  <Badge variant="outline">{stats.byCategory.data || 0}</Badge>
                </div>

                <div className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <span>⚡</span>
                    <span className="text-sm font-medium">Performance</span>
                  </div>
                  <Badge variant="outline">
                    {stats.byCategory.performance || 0}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <span>🔄</span>
                    <span className="text-sm font-medium">Sincronização</span>
                  </div>
                  <Badge variant="outline">{stats.byCategory.sync || 0}</Badge>
                </div>

                <div className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <span>🔒</span>
                    <span className="text-sm font-medium">Segurança</span>
                  </div>
                  <Badge variant="outline">
                    {stats.byCategory.security || 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Centro de Alertas Principal */}
        <AlertsCenter />

        {/* Notificações do Browser */}
        {"Notification" in window && Notification.permission !== "granted" && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-blue-600" />
                  <div>
                    <h4 className="font-medium text-blue-900">
                      Notificações do Browser
                    </h4>
                    <p className="text-sm text-blue-700">
                      Receba alertas críticos instantaneamente, mesmo com a aba
                      fechada
                    </p>
                  </div>
                </div>
                <Button
                  onClick={requestNotificationPermission}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Ativar Notificações
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </IntegrationErrorBoundary>
  );
};

export default AlertsPage;
